<template>
  <el-dialog
    title="客户端调用统计"
    :visible="visible"
    width="900px"
    custom-class="oauth-client-dialog usage-dialog"
    append-to-body
    @close="close"
    @opened="opened"
    @closed="disposeChart"
  >
    <div class="usage-client">{{ client ? client.clientName : '' }} <span>Client ID：{{ client ? client.id : '' }}</span></div>
    <div class="usage-toolbar">
      <el-date-picker
        v-model="range"
        type="daterange"
        value-format="yyyy-MM-dd"
        range-separator="至"
        start-placeholder="开始日期"
        end-placeholder="结束日期"
        :clearable="false"
        :picker-options="pickerOptions"
        popper-class="usage-date-picker"
      />
      <el-button type="primary" :loading="loading" @click="loadUsage">查询</el-button>
      <el-button class="secondary-button" icon="el-icon-refresh" :disabled="loading" @click="loadUsage">刷新</el-button>
    </div>
    <p class="usage-note">正常统计约延迟1分钟。查询范围最多366天。</p>
    <div v-loading="loading" class="usage-result">
      <el-alert v-if="error" :title="error" type="error" :closable="false" show-icon />
      <template v-else>
        <div class="usage-interface-heading">
          <span>接口调用明细</span>
          <el-button size="small" :type="selectedInterface ? 'default' : 'primary'" @click="selectInterface(null)">全部接口</el-button>
        </div>
        <el-table :data="interfaces" class="usage-table usage-interface-table" height="180" empty-text="所选日期范围内暂无接口调用记录" @row-click="selectInterface">
          <el-table-column prop="interfaceName" label="接口名称" min-width="260" show-overflow-tooltip />
          <el-table-column label="区间调用量" width="120" align="right">
            <template slot-scope="scope">{{ Number(scope.row.totalCount).toLocaleString() }}</template>
          </el-table-column>
          <el-table-column label="趋势" width="90" align="center">
            <template slot-scope="scope">
              <el-button class="usage-trend-button" type="text" @click.stop="selectInterface(scope.row)">{{ selectedInterface === scope.row ? '已选中' : '查看' }}</el-button>
            </template>
          </el-table-column>
        </el-table>
        <div class="usage-selected">{{ selectedInterface ? selectedInterface.interfaceName : '全部接口' }}</div>
        <div class="usage-total">区间调用总量 <strong>{{ loaded ? activeTotal.toLocaleString() : '—' }}</strong><span>次</span></div>
        <p v-if="loaded && total === 0" class="usage-note">所选日期范围内暂无调用记录。</p>
        <div ref="chart" class="usage-chart" />
        <el-table :data="tableRows" class="usage-table" height="250" empty-text="暂无统计数据">
          <el-table-column prop="date" label="日期" align="center" />
          <el-table-column label="调用量" align="center">
            <template slot-scope="scope">{{ Number(scope.row.callCount).toLocaleString() }}</template>
          </el-table-column>
        </el-table>
      </template>
    </div>
  </el-dialog>
</template>

<script>
import * as echarts from 'echarts';
import chartTheme from '../../mixins/chartTheme';

// 按北京时间生成日期字符串，避免浏览器时区改变查询日期。
const today = () => new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString().slice(0, 10);
const recentRange = days => {
  const end = today();
  const date = new Date(`${end}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() - days + 1);
  return [date.toISOString().slice(0, 10), end];
};
const pickerDate = value => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
};

export default {
  mixins: [chartTheme],
  props: {
    visible: { type: Boolean, default: false },
    client: { type: Object, default: null }
  },
  data() {
    return {
      range: recentRange(30), loading: false, loaded: false, error: '',
      daily: [], total: 0, interfaces: [], selectedInterface: null, requestSerial: 0, chartReady: false,
      pickerOptions: {
        disabledDate(date) { return date.getTime() > pickerDate(today()).getTime(); },
        shortcuts: [7, 30, 90].map(days => ({
          text: `最近${days}天`,
          onClick(picker) { picker.$emit('pick', recentRange(days).map(pickerDate)); }
        }))
      }
    };
  },
  computed: {
    activeDaily() { return this.selectedInterface ? this.selectedInterface.dailyCounts : this.daily; },
    activeTotal() { return Number(this.selectedInterface ? this.selectedInterface.totalCount : this.total); },
    tableRows() { return this.activeDaily.slice().reverse(); }
  },
  watch: {
    range() { this.selectInterface(null); },
    visible(value) {
      if (value) {
        this.range = recentRange(30);
        this.loadUsage();
      } else {
        this.requestSerial += 1;
        this.loading = false;
        this.chartReady = false;
      }
    },
    client() {
      if (this.visible) {
        this.range = recentRange(30);
        this.loadUsage();
      }
    }
  },
  beforeDestroy() {
    this.requestSerial += 1;
    this.disposeChart();
  },
  methods: {
    selectInterface(row) {
      this.selectedInterface = row;
      this.$nextTick(this.renderChart);
    },
    close() { this.$emit('update:visible', false); },
    opened() {
      this.chartReady = true;
      window.addEventListener('resize', this.resizeChart);
      this.renderChart();
    },
    loadUsage() {
      if (!this.client || !this.visible) return;
      if (!this.range || this.range.length !== 2) {
        this.$message.warning('请选择完整日期范围');
        return;
      }
      const days = (Date.parse(`${this.range[1]}T00:00:00Z`) - Date.parse(`${this.range[0]}T00:00:00Z`)) / 86400000 + 1;
      if (days < 1 || days > 366 || this.range[1] > today()) {
        this.$message.warning('日期范围最多366天且不能包含未来日期');
        return;
      }
      const serial = ++this.requestSerial;
      this.loading = true;
      this.loaded = false;
      this.error = '';
      this.daily = [];
      this.total = 0;
      this.interfaces = [];
      this.selectedInterface = null;
      this.disposeChart(false);
      this.$request.get(`/api/oauth/client/${this.client.id}/usage`, {
        params: { startDate: this.range[0], endDate: this.range[1] }
      }).then(response => {
        if (serial !== this.requestSerial || !this.visible) return;
        if (!response.data.result) throw new Error(response.data.message || '获取调用统计失败');
        const data = response.data.data || {};
        this.daily = data.dailyCounts || [];
        this.total = Number(data.totalCount || 0);
        this.interfaces = (data.interfaceCounts || []).map(row => ({
          ...row,
          interfaceName: row.interfaceName && row.interfaceName.trim()
            ? row.interfaceName : `${row.httpMethod} ${row.path}`
        }));
        this.loaded = true;
        this.$nextTick(this.renderChart);
      }).catch(error => {
        if (serial !== this.requestSerial || !this.visible) return;
        this.error = (error.response && error.response.data && error.response.data.message)
          || error.message || '网络异常，获取调用统计失败';
      }).finally(() => {
        if (serial === this.requestSerial) this.loading = false;
      });
    },
    renderChart() {
      if (!this.visible || !this.chartReady || !this.$refs.chart || this.error) return;
      if (!this.usageChart) this.usageChart = echarts.init(this.$refs.chart);
      this.usageChart.setOption({
        color: ['#29d8ff'],
        tooltip: { trigger: 'axis' },
        grid: { left: 24, right: 22, top: 28, bottom: 16, containLabel: true },
        xAxis: { type: 'category', data: this.activeDaily.map(row => row.date),
          axisLabel: { color: '#8dc8df' }, axisLine: { lineStyle: { color: '#3c7e9c' } } },
        yAxis: { type: 'value', minInterval: 1,
          axisLabel: { color: '#8dc8df' }, axisLine: { lineStyle: { color: '#3c7e9c' } },
          splitLine: { lineStyle: { color: 'rgba(57,166,220,0.15)' } } },
        series: [{ name: '调用量', type: 'line', showSymbol: this.activeDaily.length <= 30,
          data: this.activeDaily.map(row => row.callCount), areaStyle: { opacity: 0.12 } }]
      }, true);
      this.applyChartTheme();
      this.resizeChart();
    },
    resizeChart() { if (this.usageChart) this.usageChart.resize(); },
    disposeChart(removeListener = true) {
      if (this.usageChart) {
        this.usageChart.dispose();
        this.usageChart = null;
      }
      if (removeListener !== false) window.removeEventListener('resize', this.resizeChart);
    }
  }
};
</script>

<style>
.usage-dialog.el-dialog { max-width: calc(100vw - 32px); margin-top: 6vh !important; }
.usage-dialog .el-dialog__body { padding: 18px 22px; max-height: 78vh; overflow: auto; }
.usage-client { color: var(--solar-text-strong, #dff5ff); font-size: 16px; margin-bottom: 18px; overflow-wrap: anywhere; }
.usage-client span { color: var(--solar-text, #87bad0); font-size: 13px; margin-left: 16px; }
.usage-toolbar { display: flex; align-items: center; flex-wrap: wrap; gap: 10px; }
.usage-toolbar .el-button { margin: 0; }
.usage-dialog .el-range-editor.el-input__inner { background: var(--solar-page, #001a33); border-color: #3c7e9c; max-width: 100%; }
.usage-dialog .el-range-input { background: transparent; color: var(--solar-text-strong, #dff5ff); }
.usage-dialog .el-range-separator, .usage-dialog .el-range__icon { color: var(--solar-text, #87bad0); }
.usage-note { color: var(--solar-text, #87bad0); font-size: 12px; }
.usage-result { min-height: 120px; }
.usage-interface-heading { display: flex; justify-content: space-between; align-items: center; color: var(--solar-text-strong, #a9dbea); margin: 16px 0 10px; }
.usage-interface-table tbody tr { cursor: pointer; }
.usage-dialog .usage-trend-button { padding-left: 12px; padding-right: 12px; }
.usage-selected { margin-top: 18px; color: var(--solar-text-strong, #dff5ff); overflow-wrap: anywhere; }
.usage-total { margin: 20px 0 8px; color: var(--solar-text-strong, #a9dbea); }
.usage-total strong { font-size: 28px; color: var(--solar-primary, #29d8ff); margin: 0 12px; }
.usage-total span { font-size: 12px; }
.usage-chart { width: 100%; height: 240px; }
.usage-dialog .usage-table, .usage-dialog .usage-table th, .usage-dialog .usage-table td { background: var(--solar-surface, #032b4b); color: var(--solar-text-strong, #dff5ff); border-color: rgba(var(--solar-border-strong-rgb, 57, 166, 220), 0.25); }
.usage-dialog .usage-table tr:hover > td { background: var(--solar-raised, #075077) !important; }
.usage-dialog .usage-table::before { background: rgba(var(--solar-primary-rgb, 57, 166, 220), 0.25); }
.usage-dialog .usage-table .el-table__empty-text { color: var(--solar-text, #87bad0); }
.usage-dialog .el-loading-mask { background: rgba(var(--solar-page-rgb, 0, 22, 43), 0.8); }
.usage-date-picker.el-picker-panel { background: var(--solar-surface, #032b4b); color: var(--solar-text-strong, #dff5ff); border-color: #3c7e9c; }
.usage-date-picker .el-picker-panel__sidebar { background: var(--solar-page, #001a33); border-color: #3c7e9c; }
.usage-date-picker .el-picker-panel__shortcut, .usage-date-picker .el-picker-panel__icon-btn, .usage-date-picker .el-date-table th { color: var(--solar-text-strong, #a9dbea); }
.usage-date-picker .el-date-table td.available:hover { color: var(--solar-primary, #29d8ff); }
.usage-date-picker .el-date-table td.disabled div { background: var(--solar-raised, #163b50); color: var(--solar-muted, #6f8c9c); }
.usage-date-picker .el-date-table td.in-range div { background: var(--solar-raised, #075077); }
.usage-date-picker .el-date-range-picker__content.is-left { border-color: #3c7e9c; }
@media (max-width: 600px) { .usage-date-picker .el-picker-panel__sidebar { display: none; } .usage-date-picker { max-width: calc(100vw - 16px); overflow-x: auto; } .usage-date-picker .el-picker-panel__body { margin-left: 0; } }
</style>
